/**
 * G2eGente - Histórico de Avaliações de Desempenho
 * Timeline visual mostrando a evolução das avaliações do funcionário
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Loader2,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Star,
  Award,
  Target,
  ChevronRight,
  BarChart3,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const CLASSIFICACAO_COLORS = {
  'excepcional': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-500' },
  'supera_expectativas': { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500' },
  'atende_expectativas': { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' },
  'parcialmente_atende': { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-500' },
  'nao_atende': { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500' }
};

const CLASSIFICACAO_LABELS = {
  'excepcional': 'Excepcional',
  'supera_expectativas': 'Supera Expectativas',
  'atende_expectativas': 'Atende Expectativas',
  'parcialmente_atende': 'Parcialmente Atende',
  'nao_atende': 'Não Atende'
};

export default function EmployeePerformanceHistory() {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, [employeeId]);

  const fetchData = async () => {
    try {
      const [empRes, histRes] = await Promise.all([
        employeesAPI.getById(employeeId),
        employeesAPI.getPerformanceHistory(employeeId)
      ]);
      setEmployee(empRes.data);
      setHistory(histRes.data.history || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      if (error.response?.status === 404) {
        toast.error('Funcionário não encontrado');
        navigate('/company/employees');
        return;
      }
      toast.error('Erro ao carregar histórico de avaliações');
    } finally {
      setLoading(false);
    }
  };

  const getTrend = (index) => {
    if (index >= history.length - 1) return null;
    const current = history[index].media_geral || 0;
    const previous = history[index + 1].media_geral || 0;
    if (current > previous) return 'up';
    if (current < previous) return 'down';
    return 'stable';
  };

  const getAverageScore = () => {
    if (history.length === 0) return 0;
    const total = history.reduce((sum, h) => sum + (h.media_geral || 0), 0);
    return (total / history.length).toFixed(1);
  };

  const getBestScore = () => {
    if (history.length === 0) return 0;
    return Math.max(...history.map(h => h.media_geral || 0)).toFixed(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/company/employees')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Histórico de Avaliações
            </h1>
            <p className="text-slate-600">
              {employee?.nome} - {employee?.cargo}
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-cyan-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total de Avaliações</p>
                  <p className="text-2xl font-bold text-slate-900">{history.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Target className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Média Geral</p>
                  <p className="text-2xl font-bold text-slate-900">{getAverageScore()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Award className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Melhor Nota</p>
                  <p className="text-2xl font-bold text-slate-900">{getBestScore()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Última Avaliação</p>
                  <p className="text-lg font-bold text-slate-900">
                    {history.length > 0 ? formatDate(history[0].created_at) : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Linha do Tempo de Avaliações
            </CardTitle>
            <CardDescription>
              Evolução do desempenho ao longo do tempo
            </CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma avaliação finalizada encontrada</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate(`/company/employees/${employeeId}/evaluate`)}
                >
                  Criar Primeira Avaliação
                </Button>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200" />

                {/* Timeline items */}
                <div className="space-y-6">
                  {history.map((item, index) => {
                    const trend = getTrend(index);
                    const colors = CLASSIFICACAO_COLORS[item.classificacao] || CLASSIFICACAO_COLORS.atende_expectativas;
                    
                    return (
                      <div key={item.review_id} className="relative pl-16">
                        {/* Timeline dot with trend indicator */}
                        <div className={`absolute left-4 w-5 h-5 rounded-full ${colors.bg} ${colors.border} border-2 flex items-center justify-center`}>
                          {trend === 'up' && <TrendingUp className="w-3 h-3 text-emerald-600" />}
                          {trend === 'down' && <TrendingDown className="w-3 h-3 text-red-600" />}
                          {trend === 'stable' && <Minus className="w-3 h-3 text-slate-400" />}
                        </div>

                        {/* Content card */}
                        <Card className={`border-l-4 ${colors.border} hover:shadow-md transition-shadow`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-semibold text-slate-900">
                                    {item.cycle_nome || 'Ciclo de Avaliação'}
                                  </h3>
                                  <Badge variant="outline" className={`${colors.bg} ${colors.text}`}>
                                    {CLASSIFICACAO_LABELS[item.classificacao] || item.classificacao}
                                  </Badge>
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {item.periodo || formatDate(item.created_at)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Target className="w-4 h-4" />
                                    Tipo: {item.tipo === 'autoavaliacao' ? 'Autoavaliação' : 
                                           item.tipo === 'gestor' ? 'Avaliação do Gestor' : 
                                           item.tipo === '360' ? 'Avaliação 360°' : item.tipo}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-4">
                                {/* Score visualization */}
                                <div className="text-center">
                                  <div className="text-3xl font-bold text-slate-900">
                                    {item.media_geral?.toFixed(1) || '-'}
                                  </div>
                                  <div className="text-xs text-slate-500">Nota</div>
                                </div>

                                {/* Progress bar */}
                                <div className="w-24 hidden sm:block">
                                  <Progress 
                                    value={(item.media_geral || 0) * 20} 
                                    className="h-2"
                                  />
                                </div>

                                {/* View details button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/company/performance/review/${item.review_id}`)}
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/company/employees')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Lista
          </Button>
          <Button
            onClick={() => navigate(`/company/employees/${employeeId}/evaluate`)}
            className="bg-cyan-600 hover:bg-cyan-700"
          >
            Nova Avaliação
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
